import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

const getPassId = (booking) => {
  const raw = booking.passId;
  if (!raw) return null;
  return typeof raw === "object" ? raw._id : raw;
};

// Groups bookings by their passId, rendering each pass as a collapsible section.
// Bookings with no resolvable pass are grouped into a trailing "other" section.
export default function BookingsByPass({
  bookings,
  passes = [],
  translatePassName,
  renderRow,
  emptyMessage,
  otherLabel = "Other bookings",
  classesCountLabel = "classes",
}) {
  const [openGroups, setOpenGroups] = useState(() => new Set());

  if (!bookings || bookings.length === 0) {
    return <p className="text-gray-500 text-sm py-4">{emptyMessage}</p>;
  }

  const passById = new Map(passes.map((p) => [String(p._id), p]));

  const groups = new Map();
  bookings.forEach((booking) => {
    const passId = getPassId(booking);
    const key = passId && passById.has(String(passId)) ? String(passId) : "other";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(booking);
  });

  const orderedGroupKeys = [...groups.keys()]
    .filter((k) => k !== "other")
    .sort((a, b) => (groups.get(b)[0]?.sortDate ?? 0) - (groups.get(a)[0]?.sortDate ?? 0));
  if (groups.has("other")) orderedGroupKeys.push("other");

  const toggleGroup = (key) => {
    setOpenGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className="space-y-2">
      {orderedGroupKeys.map((key) => {
        const groupBookings = groups.get(key);
        const pass = key !== "other" ? passById.get(key) : null;
        const label = pass
          ? translatePassName(pass.passTypeId || pass.name)
          : otherLabel;
        const isOpen = openGroups.has(key);

        return (
          <div key={key} className="rounded-xl border border-gray-100 overflow-hidden">
            <button
              type="button"
              onClick={() => toggleGroup(key)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2.5 bg-gray-50 hover:bg-amber-50/50 transition text-left"
            >
              <span className="font-medium text-gray-800 truncate">{label}</span>
              <span className="flex items-center gap-2 shrink-0 text-sm text-gray-500">
                {groupBookings.length} {classesCountLabel}
                {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
              </span>
            </button>
            {isOpen && (
              <ul className="divide-y divide-gray-100">
                {groupBookings.map((booking) => (
                  <li key={booking.key}>{renderRow(booking)}</li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
